'use client';

import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { getKnownCarBrandLogo } from '@/lib/car-brand-logos';

type BrandLike = {
  id?: string;
  name_en?: string;
  name_ar?: string;
  logo_url?: string;
};

interface BrandLogoImageProps {
  brand?: BrandLike | null;
  alt?: string;
  className?: string;
}

const LOCAL_FALLBACK_LOGO = '/maknna_logo.png';

export default function BrandLogoImage({ brand, alt = '', className }: BrandLogoImageProps) {
  const knownLogo = useMemo(
    () => getKnownCarBrandLogo(brand?.id, brand?.name_en, brand?.name_ar),
    [brand?.id, brand?.name_ar, brand?.name_en]
  );

  const primaryLogo = (brand?.logo_url || '').trim();
  const initialLogo = knownLogo || primaryLogo || LOCAL_FALLBACK_LOGO;
  const [src, setSrc] = useState(initialLogo);

  useEffect(() => {
    setSrc(initialLogo);
  }, [initialLogo]);

  const handleError = () => {
    setSrc((current) => {
      if (primaryLogo && current !== primaryLogo) return primaryLogo;
      if (current !== LOCAL_FALLBACK_LOGO) return LOCAL_FALLBACK_LOGO;
      return current;
    });
  };

  return (
    <img
      src={src}
      alt={alt}
      className={cn('object-contain', className)}
      referrerPolicy="no-referrer"
      onError={handleError}
      loading="lazy"
      decoding="async"
    />
  );
}
